// 연계 안내 줄(LIVE-FUN-PLAN 3.0d·대표님 2026-10-20 "스킬 콤보개념이 없어"): 첫 3단 연계에 성공할 때까지 던전 화면 위쪽 가장자리에
// 얇은 줄로 "연계  돌진 → 폭풍 → 낙화"를 띄우고, 이번 연계에서 쓴 스킬을 금빛으로 켠다. 첫 피니시 뒤에는 저장해 다시 띄우지 않는다.
// 화면 가운데·영웅을 가리지 않도록 아래 가장자리 가운데(조작 줄 바로 위)에 높이 26px 줄 하나만 둔다(2026-10-27, 위 가운데는 보스 체력 줄과 겹쳤다).
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import { viewH, viewW } from '../shared/viewport';
import { FONT, el } from './dom';
import { LINK_HINT_H, linkHintBottom, linkHintLayout, type HudRect } from './skillHudLayout';
import { raidHintRect } from './raidHudLayout';

export const LINK_HINT_KEY = 'dungeonhero.linkHint.v1';
const STEPS = [
  { key: 'U', name: '돌진' },
  { key: 'I', name: '폭풍' },
  { key: 'O', name: '낙화' },
] as const;
/** 라이라 첫 세 스킬(c022): 3연사 → 백스텝 → 관통 */
const LYRA_NAMES = ['3연사', '백스텝', '관통'] as const;
const ON = 'color:#ffe07a;text-shadow:0 0 8px rgba(255,190,60,.9),0 1px 1px #000;';
const OFF = 'color:#d8dcea;text-shadow:0 1px 1px #000;';

/** 저장소에서 안내를 끝냈는지 읽는다(저장소가 막혀 있으면 계속 보여 준다) */
export function linkHintDone(storage: Pick<Storage, 'getItem'> | null): boolean {
  try { return storage?.getItem(LINK_HINT_KEY) === 'done'; } catch { return false; }
}

export class LinkHint {
  private readonly root: HTMLDivElement;
  private readonly parts: HTMLSpanElement[] = [];
  private done: boolean;
  private shown = false;
  private lastUsed = -1;
  private readonly touch: boolean;
  private hero = 'kael';

  constructor(parent: HTMLElement, private readonly view: GameView, touch: boolean) {
    // 웹뷰·차단된 사이트 데이터에서는 localStorage에 손대는 것만으로 SecurityError가 난다(c014): 읽기 실패면 안내를 다시 보인다
    let store: Storage | null = null;
    try { store = typeof localStorage === 'undefined' ? null : localStorage; } catch { store = null; }
    this.done = linkHintDone(store);
    this.touch = touch;
    // HUD 정리(2026-10-27): 위 가운데는 보스·대상 체력 줄 자리라 겹쳤다 → 아래 가운데, 조작 줄(터치 물약 줄·PC 스킬 바) 바로 위
    this.root = el('div', 'position:absolute;left:50%;bottom:' + linkHintBottom(touch) + 'px;transform:translateX(-50%);display:none;align-items:center;gap:8px;height:26px;padding:0 14px;box-sizing:border-box;' +
      'border-radius:13px;background:rgba(14,20,40,.72);border:1px solid rgba(214,178,94,.7);pointer-events:none;white-space:nowrap;font:800 13px ' + FONT + ';letter-spacing:.5px;', parent);
    this.root.dataset.testid = 'link-hint';
    el('span', 'color:#ffcf5a;font-weight:900;margin-right:2px;', this.root, '연계');
    STEPS.forEach((s, i) => {
      if (i > 0) el('span', 'color:#8c93ad;', this.root, '→');
      this.parts.push(el('span', OFF, this.root, touch ? s.name : s.key + ' ' + s.name));
    });
  }

  onEvent(e: GameEvent): void {
    if (e.type !== Ev.LinkFinish || this.done) return;
    this.done = true;
    try { localStorage.setItem(LINK_HINT_KEY, 'done'); } catch { /* 저장 실패면 다음 판에 다시 보인다 */ }
  }

  /** 피할 사각형(0: 자동 이동 알약, 1: 요령). 프레임 루프에서 새 객체를 만들지 않도록 재사용한다 */
  private readonly obs: HudRect[] = [{ x: 0, y: 0, w: 0, h: 0 }, { x: 0, y: 0, w: 0, h: 0 }];
  private kW = -1;
  private kH = -1;
  private kHw = -1;

  private raid = false;
  private raidKey = '';
  private readonly raidRect: HudRect = { x: 0, y: 0, w: 0, h: 0 };

  /**
   * 레이드 장면(2026-10-30 L11): 가운데 아래 자리는 영웅·용병 발밑이라 비운다. 왼쪽 위 영웅 패널 아래 빈 줄로 옮기고(raidHintRect),
   * 빈 줄이 모자라면 접는다. 레이드가 아니면 원래 자리 계산(place)으로 돌아간다.
   */
  setRaid(on: boolean): void {
    if (on === this.raid) return;
    this.raid = on;
    this.raidKey = '';
    const s = this.root.style;
    if (on) { s.transform = 'none'; s.bottom = 'auto'; s.left = '8px'; }
    else { s.left = '50%'; s.transform = 'translateX(-50%)'; s.visibility = 'visible'; s.top = 'auto'; s.bottom = linkHintBottom(this.touch) + 'px'; this.kW = -1; }
  }
  get inRaid(): boolean { return this.raid; }

  /** 레이드 자리: 영웅 패널(topLeft)과 목표 카드 위(objectiveTop) 사이, 보스 체력 줄·한 줄 파티 줄(obstacles)은 비킨다. 값이 바뀔 때만 쓴다 */
  placeRaid(topLeft: HudRect, objectiveTop: number, obstacles: readonly HudRect[] = [], below: { top: number; limit: number } | null = null): void {
    if (!this.raid || !this.shown) return;
    const hw = this.root.offsetWidth;
    let key = Math.round(topLeft.y + topLeft.h) + ':' + Math.round(objectiveTop) + ':' + hw;
    for (const o of obstacles) key += ':' + Math.round(o.x) + ',' + Math.round(o.y) + ',' + Math.round(o.w) + ',' + Math.round(o.h);
    if (below) key += ':b' + Math.round(below.top) + ',' + Math.round(below.limit);
    if (key === this.raidKey) return;
    this.raidKey = key;
    const r = this.raidRect, ok = raidHintRect(topLeft, objectiveTop, hw, LINK_HINT_H, obstacles, below, r);
    const s = this.root.style;
    s.visibility = ok ? 'visible' : 'hidden';
    if (ok) { s.left = r.x + 'px'; s.top = r.y + 'px'; }
  }

  /** 안내 줄이 보이는 중인지(첫 3단 연계 전·던전 전투 중). 아니면 place를 부를 필요가 없다 */
  get visible(): boolean { return this.shown; }

  /**
   * 보이는 자동 이동 알약·요령을 피해 자리를 고른다(linkHintLayout). 568×320에서 가운데로 올라온 알약이 기본 자리를 덮었다(2026-10-27 배포 확인).
   * HUD가 안내 줄이 보일 때만 몇 프레임마다 부른다. 사각형은 offset*(배율 전 레이아웃 좌표, 돌린 화면에서도 같은 좌표계)로 읽어 객체를 만들지 않고,
   * 화면 크기·사각형이 그대로면 아무것도 하지 않는다. 기본 자리면 원래 bottom 배치로 둔다.
   */
  place(status: HTMLElement | null, tip: HTMLElement | null, tipScale: number): void {
    if (!this.shown) return;
    const W = viewW(), H = viewH(), hw = this.root.offsetWidth;
    if (!hw) return;
    const a = this.obs[0], b = this.obs[1];
    let changed = W !== this.kW || H !== this.kH || hw !== this.kHw;
    changed = readRect(status, 1, a) || changed;
    changed = readRect(tip, tipScale, b) || changed;
    if (!changed) return;
    this.kW = W; this.kH = H; this.kHw = hw;
    const L = linkHintLayout(W, H, this.touch, hw, this.obs);
    const s = this.root.style;
    s.visibility = L.visible ? 'visible' : 'hidden';
    if (L.moved) { s.bottom = 'auto'; s.top = L.y + 'px'; } else { s.top = 'auto'; s.bottom = linkHintBottom(this.touch) + 'px'; }
  }

  /** 매 프레임. live: 던전 전투 중 */
  update(live: boolean): void {
    const show = live && !this.done;
    if (show !== this.shown) { this.shown = show; this.root.style.display = show ? 'flex' : 'none'; }
    if (!show) return;
    // 영웅이 바뀌면 스킬 이름을 그 영웅 것으로(c022)
    const hero = this.view.heroId ?? 'kael';
    if (hero !== this.hero) {
      this.hero = hero;
      for (let i = 0; i < this.parts.length; i++) {
        const name = hero === 'lyra' ? LYRA_NAMES[i] : STEPS[i].name;
        this.parts[i].textContent = this.touch ? name : STEPS[i].key + ' ' + name;
      }
    }
    const used = this.view.link ? this.view.link.used : 0;
    if (used === this.lastUsed) return;
    this.lastUsed = used;
    for (let i = 0; i < this.parts.length; i++) this.parts[i].style.cssText = (used >> i) & 1 ? ON : OFF;
  }
}


/** 보이는 요소의 레이아웃 사각형을 out에 담는다(숨었으면 크기 0). 값이 바뀌었으면 true */
function readRect(e: HTMLElement | null, scale: number, out: HudRect): boolean {
  const on = !!e && e.style.display !== 'none' && e.offsetWidth > 0 && e.offsetHeight > 0;
  const x = on && e ? e.offsetLeft : 0, y = on && e ? e.offsetTop : 0;
  const w = on && e ? Math.round(e.offsetWidth * scale) : 0, h = on && e ? Math.round(e.offsetHeight * scale) : 0;
  if (x === out.x && y === out.y && w === out.w && h === out.h) return false;
  out.x = x; out.y = y; out.w = w; out.h = h;
  return true;
}
